import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { shortBaseballStats } from '@sorare/core/src/lib/baseball';
import { shortNbaStats } from '@sorare/core/src/lib/nba';
import { Stats, statLabels } from '@sorare/core/src/lib/scoring';

type Props = {
  stat: string;
};

const CapitalizeFirstLetter = styled(LabelS)`
  &::first-letter {
    text-transform: uppercase;
  }
`;

export const StatCellLabel = ({ stat }: Props) => {
  const { sport } = useSportContext();

  const label = useMemo(() => {
    switch (sport) {
      case Sport.BASEBALL:
        return (
          <LabelS>
            {shortBaseballStats[stat as keyof typeof shortBaseballStats]}
          </LabelS>
        );
      case Sport.NBA:
        return (
          <LabelS>{shortNbaStats[stat as keyof typeof shortNbaStats]}</LabelS>
        );
      case Sport.FOOTBALL:
        return <StatIcon statKey={stat.toLowerCase() as Stats} />;
      default:
        return null;
    }
  }, [sport, stat]);

  return (
    <Tooltip
      title={
        <CapitalizeFirstLetter>
          <FormattedMessage
            {...statLabels[stat.toLowerCase() as keyof typeof statLabels]}
          />
        </CapitalizeFirstLetter>
      }
    >
      {label}
    </Tooltip>
  );
};
